import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function BuffettPhilosophy({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-4xl mx-auto">
        
        <Button 
          variant="ghost" 
          onClick={() => navigate('/learn')} 
          className="mb-6 dark:text-gray-300"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Learn Home
        </Button>

        <Card className="glass p-8 mb-6 dark:bg-gray-800">
          <div className="flex items-center space-x-2 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
              Advanced
            </span>
            <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-4 h-4 mr-1" />
              15 min read
            </span>
          </div>

          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            🎯 Warren Buffett's Investment Philosophy
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Understand the principles that made Buffett one of the greatest investors
          </p>

          <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
            
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Who is Warren Buffett?</h2>
              <p className="mb-4">
                Warren Buffett, age 94, is worth over $120 billion. But what's remarkable is that <strong>99% of his wealth was built after age 50</strong> through disciplined, patient investing.
              </p>
              <p className="mb-4">
                He started investing at age 11 with $114. Today, his company Berkshire Hathaway is one of the most valuable in the world. 
                His secret? <strong>Not luck or genius - but a simple, consistent philosophy applied over decades.</strong>
              </p>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
                <p className="text-sm">
                  <strong>📚 The Oracle of Omaha:</strong> Buffett lives in the same house he bought in 1958 for $31,500 in Omaha, Nebraska. 
                  He still drives himself to work. Proof that wealth comes from discipline, not lifestyle inflation.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Buffett's Core Principles</h2>
              
              <div className="space-y-4">
                <div className="p-5 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-500">
                  <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-2">1. Buy Wonderful Companies at Fair Prices</h3>
                  <p className="text-sm mb-3">
                    <em>"It's far better to buy a wonderful company at a fair price than a fair company at a wonderful price."</em>
                  </p>
                  <p className="text-sm mb-2"><strong>What this means:</strong></p>
                  <p className="text-sm mb-2">
                    Early Buffett looked for "cigar butt" stocks - terrible businesses trading cheap. He'd make a quick profit and move on. 
                    After meeting Charlie Munger, he shifted: Buy great businesses with competitive advantages ("moats") and hold forever.
                  </p>
                  <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                    🇮🇳 Indian Example: TCS, Asian Paints, HDFC Bank - quality businesses with strong moats. 
                    Better to pay ₹3,500 for TCS than ₹50 for a dying textile company.
                  </p>
                </div>

                <div className="p-5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-blue-500">
                  <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-2">2. Look for Economic Moats</h3>
                  <p className="text-sm mb-3">
                    <em>"In business, I look for economic castles protected by unbreachable moats."</em>
                  </p>
                  <p className="text-sm mb-2"><strong>What is a moat?</strong></p>
                  <p className="text-sm mb-2">
                    A sustainable competitive advantage that protects the company from competitors. Like a castle moat keeps enemies out, 
                    an economic moat keeps competitors from stealing market share.
                  </p>
                  <p className="text-sm mb-2"><strong>Types of Moats:</strong></p>
                  <ul className="text-sm space-y-1 list-disc pl-5">
                    <li><strong>Brand Power:</strong> Asian Paints, Titan - customers prefer them despite higher prices</li>
                    <li><strong>Network Effects:</strong> More users = more valuable (IRCTC, Zomato)</li>
                    <li><strong>Cost Advantage:</strong> Lowest cost producer (Maruti in small cars)</li>
                    <li><strong>High Switching Costs:</strong> Hard to change providers (TCS enterprise contracts)</li>
                  </ul>
                </div>

                <div className="p-5 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-2 border-purple-500">
                  <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-2">3. Invest Within Your Circle of Competence</h3>
                  <p className="text-sm mb-3">
                    <em>"Never invest in a business you cannot understand."</em>
                  </p>
                  <p className="text-sm mb-2">
                    Buffett famously avoided tech stocks during the dot-com bubble (1999-2000) because he "didn't understand them." 
                    Everyone thought he was outdated. Tech crashed 80%. Buffett was proven right.
                  </p>
                  <p className="text-sm mb-2"><strong>Circle of Competence in India:</strong></p>
                  <p className="text-sm">
                    If you're an engineer, you might understand TCS, Infosys better. If you're in finance, HDFC Bank makes sense. 
                    A teacher might understand D-Mart's retail model. <strong>Stick to what you know.</strong> Don't buy biotech if you don't understand drug trials.
                  </p>
                </div>

                <div className="p-5 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-2 border-yellow-500">
                  <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-2">4. Be Fearful When Others Are Greedy</h3>
                  <p className="text-sm mb-3">
                    <em>"Be fearful when others are greedy, and greedy when others are fearful."</em>
                  </p>
                  <p className="text-sm mb-2"><strong>Real Indian Example - March 2020:</strong></p>
                  <div className="space-y-2 mt-2">
                    <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded text-sm">
                      <strong>Panic (March 2020):</strong> Nifty crashed from 12,000 to 7,500. Everyone was selling in fear. 
                      "Market will go to 5,000!" screamed the headlines.
                    </div>
                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded text-sm">
                      <strong>Opportunity:</strong> Buffett-style investors bought quality stocks at huge discounts. 
                      TCS at ₹1,800, Reliance at ₹800, HDFC at ₹1,400.
                    </div>
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-sm">
                      <strong>Result (6 months later):</strong> Nifty back at 12,000. Those who bought in panic made 50-60% returns!
                    </div>
                  </div>
                  <p className="text-sm mt-3 font-semibold">
                    Lesson: The best time to buy is when everyone is panicking. The worst time is when everyone is euphoric.
                  </p>
                </div>

                <div className="p-5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-2 border-indigo-500">
                  <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-2">5. Think Like an Owner, Not a Trader</h3>
                  <p className="text-sm mb-3">
                    <em>"If you aren't willing to own a stock for 10 years, don't even think about owning it for 10 minutes."</em>
                  </p>
                  <p className="text-sm mb-2">
                    When you buy a stock, you're buying a piece of a business. Would you buy and sell a shop every week based on mood? 
                    No! Then why trade stocks daily?
                  </p>
                  <p className="text-sm">
                    Buffett bought Coca-Cola in 1988 and still owns it 36 years later. His holding period: <strong>Forever.</strong> 
                    In India, think similarly about TCS, HDFC, Asian Paints - own for decades, not days.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Buffett's Biggest Wins (Lessons for Indian Investors)</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Investment</th>
                      <th className="p-3 text-right">Year Bought</th>
                      <th className="p-3 text-right">Return</th>
                      <th className="p-3 text-left">Key Lesson</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Coca-Cola</td>
                      <td className="p-3 text-right">1988</td>
                      <td className="p-3 text-right font-bold text-green-600">2,000%+</td>
                      <td className="p-3">Brand moat + global reach</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">American Express</td>
                      <td className="p-3 text-right">1964</td>
                      <td className="p-3 text-right font-bold text-green-600">5,000%+</td>
                      <td className="p-3">Bought during scandal, trusted brand would recover</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Apple</td>
                      <td className="p-3 text-right">2016</td>
                      <td className="p-3 text-right font-bold text-green-600">600%+</td>
                      <td className="p-3">Strong brand + ecosystem + loyal customers</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded">
                <p className="text-sm font-semibold mb-2">Indian Parallels:</p>
                <ul className="text-sm space-y-1 list-disc pl-5">
                  <li><strong>Asian Paints = Coca-Cola:</strong> Dominant brand, pricing power, growing market</li>
                  <li><strong>HDFC Bank = American Express:</strong> Trust, brand, financial moat</li>
                  <li><strong>TCS = Apple:</strong> Quality, global reach, high switching costs</li>
                </ul>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">What Buffett Avoids</h2>
              
              <div className="space-y-3">
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border-l-4 border-red-500">
                  <p className="text-sm"><strong>❌ IPOs:</strong> "If you're offered a seat on a rocket ship, don't ask what seat. Just get on." - NOT Buffett! 
                  He avoids IPOs. They're usually overpriced. Wait 2-3 years, see real performance.</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border-l-4 border-red-500">
                  <p className="text-sm"><strong>❌ Commodity Businesses:</strong> Steel, airlines, textiles - no moat, intense competition, low margins. 
                  He famously said airlines "destroy shareholder value."</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border-l-4 border-red-500">
                  <p className="text-sm"><strong>❌ Leverage/Debt:</strong> Never borrow to invest. Berkshire Hathaway has almost zero debt. 
                  Debt can wipe you out in a downturn.</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border-l-4 border-red-500">
                  <p className="text-sm"><strong>❌ Chasing Hot Stocks:</strong> Crypto, meme stocks, "next big thing" - Buffett stays away. 
                  He missed the dot-com bubble on purpose!</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">How to Apply Buffett's Philosophy in India</h2>
              
              <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl">
                <p className="font-bold text-lg text-gray-800 dark:text-gray-100 mb-3">The Buffett Checklist for Indian Stocks:</p>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✅</span>
                    <p className="text-sm"><strong>Does it have a moat?</strong> Brand, network effects, cost advantage, high switching costs?</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✅</span>
                    <p className="text-sm"><strong>Do I understand the business?</strong> Can I explain it to a 10-year-old?</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✅</span>
                    <p className="text-sm"><strong>Is management trustworthy?</strong> Check promoter history, corporate governance.</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✅</span>
                    <p className="text-sm"><strong>Is ROE consistently above 15%?</strong> Sign of quality business.</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✅</span>
                    <p className="text-sm"><strong>Is debt-to-equity below 1?</strong> Financial safety.</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✅</span>
                    <p className="text-sm"><strong>Am I getting it at a fair price?</strong> Not necessarily cheap, but reasonable P/E for growth.</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✅</span>
                    <p className="text-sm"><strong>Can I hold for 10+ years?</strong> If not, don't buy.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-700">
              <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-3">💡 Key Takeaways</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Quality over price:</strong> Buy wonderful businesses at fair prices, not mediocre ones at cheap prices</li>
                <li><strong>Look for moats:</strong> Competitive advantages that protect the business for decades</li>
                <li><strong>Stay within competence:</strong> Only invest in businesses you truly understand</li>
                <li><strong>Be contrarian:</strong> Buy when others panic, sell when others are euphoric</li>
                <li><strong>Think long-term:</strong> Your holding period should be forever if you picked right</li>
                <li><strong>Patience wins:</strong> 99% of Buffett's wealth came after age 50. Compounding takes time!</li>
              </ul>
            </div>

          </div>
        </Card>

        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/learn/intrinsic-value')}
            className="dark:bg-gray-800 dark:text-gray-100"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous: Intrinsic Value
          </Button>
          <Button 
            onClick={() => navigate('/learn/margin-of-safety')}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
          >
            Next: Margin of Safety
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

      </div>
      <Footer />
    </div>
  );
}
